import type { FilterSearchConfig, FilterSearchTrigger } from "./types";
import type { ComputedRef } from "vue";
import { watchDebounced } from "@vueuse/core";

const DEFAULT_PER_PAGE = 30;
const DEFAULT_DEBOUNCE_MS = 800;

/**
 * Generic composable for fetching search results with debounced reactive updates.
 * First load is immediate; subsequent changes are debounced.
 * Previous requests are aborted when new ones are initiated.
 */
export function useFilterSearch<TItem, TRequest, TTrigger extends FilterSearchTrigger = FilterSearchTrigger>(searchTrigger: ComputedRef<TTrigger>, config: FilterSearchConfig<TItem, TRequest>) {
  const { buildRequest, executeSearch, perPage = DEFAULT_PER_PAGE, debounceMs = DEFAULT_DEBOUNCE_MS } = config;

  const items = ref<TItem[]>([]);
  const totalItems = ref(0);
  const isLoading = ref(false);
  const error = ref<Error | null>(null);

  // AbortController for request cancellation
  let abortController: AbortController | null = null;

  const fetchResults = async (filterValues: Record<string, string | string[] | undefined>, page: number) => {
    // Cancel previous request
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    isLoading.value = true;
    error.value = null;

    try {
      const request = await buildRequest(filterValues, page, perPage);
      const response = await executeSearch(request, { signal: abortController.signal });

      items.value = response.items;
      totalItems.value = response.totalItems;
    } catch (e) {
      // Ignore abort errors
      if (e instanceof Error && e.name === "AbortError") {
        return;
      }
      // Axios wraps abort errors differently
      if (e instanceof Error && e.message === "canceled") {
        return;
      }
      error.value = e instanceof Error ? e : new Error("Unknown error");
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  // Track if first load
  let isFirstLoad = true;

  // Watch for changes with debounce (skip debounce on first load)
  watch(
    searchTrigger,
    (trigger) => {
      if (isFirstLoad) {
        isFirstLoad = false;
        fetchResults(trigger.filterValues, trigger.page);
      }
      // Debounced fetch will be triggered by watchDebounced below
    },
    { immediate: true }
  );

  // Debounced watcher for subsequent changes
  watchDebounced(
    searchTrigger,
    (trigger) => {
      if (!isFirstLoad) {
        fetchResults(trigger.filterValues, trigger.page);
      }
    },
    { debounce: debounceMs, immediate: false }
  );

  const refetch = () => {
    const trigger = searchTrigger.value;
    fetchResults(trigger.filterValues, trigger.page);
  };

  return {
    items,
    totalItems,
    isLoading,
    error,
    refetch,
  };
}
